import React, { useCallback, useEffect, useRef, useState } from 'react';

import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import { cn } from '@/lib/utils';
import { useBoard, useDialogs, useInfiniteScroll } from '../../../hooks';
import { taskStatusShape } from '../../../types';
import Header from './Header';
import TaskCard from './TaskCard';

const TASK_ROW_STYLES = {
  containIntrinsicHeight: 'auto 44px',
  contentVisibility: 'auto',
};

const StatusGroup = ({ status }) => {
  const ref = useRef(null);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  const { board, fetchMoreTasks } = useBoard();
  const { handleDeleteTaskClick, handleEditTaskClick } = useDialogs();

  const {
    tasks,
    pagination: { hasMore, isLoadingMore, totalTasks },
  } = board[status.id];

  const handleLoadMore = useCallback(() => {
    fetchMoreTasks(status.id);
  }, [fetchMoreTasks, status.id]);

  const sentinelRef = useInfiniteScroll({
    hasMore: hasMore && !isCollapsed,
    isLoading: isLoadingMore,
    onLoadMore: handleLoadMore,
  });

  const dropTargetStyles = cn('border-b last:border-b-0', isDraggedOver && 'bg-hover');

  useEffect(() => {
    const element = ref.current;

    const handleCanDrop = ({ source }) => {
      // Ensure that only TaskCards can be dropped into the StatusGroup.
      return source.data?.type === 'task';
    };

    return dropTargetForElements({
      element,
      getData: () => ({ id: status.id }),
      canDrop: handleCanDrop,
      onDragEnter: () => setIsDraggedOver(true),
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: () => setIsDraggedOver(false),
    });
  }, [status.id]);

  return (
    <div className={dropTargetStyles} ref={ref}>
      <Header
        isCollapsed={isCollapsed}
        onToggle={() => setIsCollapsed((prev) => !prev)}
        status={status}
        taskCount={totalTasks}
      />

      {!isCollapsed && (
        <>
          {tasks.map((task) => (
            <div key={task.id} style={TASK_ROW_STYLES}>
              <TaskCard
                onDeleteClick={() => handleDeleteTaskClick(task)}
                onEditClick={() => handleEditTaskClick(task)}
                statusId={status.id}
                task={task}
              />
            </div>
          ))}

          {hasMore && <div className="h-px" ref={sentinelRef} />}
        </>
      )}
    </div>
  );
};

StatusGroup.propTypes = {
  status: taskStatusShape.isRequired,
};

export default StatusGroup;
